import { z } from 'zod';

/** Fixed section library identifiers (18 sections). */
export const SECTION_IDS = [
  'hero',
  'engagement-story',
  'vbp-engagement',
  'who-were-reaching',
  'what-members-need',
  'connections-that-matter',
  'quality-measure-projections',
  'connections-behavioral-care',
  'bh-medical-diagnoses',
  'clinical-care-outcomes',
  'connections-primary-care',
  'harp-enrollment',
  'gap-closures',
  'home-visits',
  'member-spotlight',
  'closing-value',
  'whats-possible-at-maturity',
  'looking-ahead',
] as const;

/** Section identifier from the fixed report library. */
export type SectionId = (typeof SECTION_IDS)[number];

/** Zod schema for {@link SectionId}. */
export const SectionIdSchema = z.enum(SECTION_IDS);

export const PAYER_TYPES = [
  'UHC',
  'Aetna',
  'BCBS',
  'Humana',
  'Other',
] as const;

/** Customer payer label. */
export type PayerType = (typeof PAYER_TYPES)[number];

/** Zod schema for {@link PayerType}. */
export const PayerTypeSchema = z.enum(PAYER_TYPES);

export const PRODUCT_CONFIGS = [
  'behavioral-health',
  'primary-care',
  'quality-measures',
  'vbp',
] as const;

/** Product capability flags configured per customer. */
export type ProductConfig = (typeof PRODUCT_CONFIGS)[number];

/** Zod schema for {@link ProductConfig}. */
export const ProductConfigSchema = z.enum(PRODUCT_CONFIGS);

export const CADENCES = ['monthly', 'quarterly', 'adhoc'] as const;

/** Report publishing cadence. */
export type Cadence = (typeof CADENCES)[number];

/** Zod schema for {@link Cadence}. */
export const CadenceSchema = z.enum(CADENCES);

export const REPORT_STATUSES = ['active', 'archived'] as const;

/** Lifecycle status of a report instance. */
export type ReportStatus = (typeof REPORT_STATUSES)[number];

/** Zod schema for {@link ReportStatus}. */
export const ReportStatusSchema = z.enum(REPORT_STATUSES);

/** Default overline for a clinical insight card. */
export const DEFAULT_CLINICAL_INSIGHT_OVERLINE = 'CLINICAL INSIGHT';

/** Default overline for a financial insight card. */
export const DEFAULT_FINANCIAL_INSIGHT_OVERLINE = 'FINANCIAL INSIGHT';

/**
 * Returns the default overline for an insight card when none is stored.
 * @param card - Clinical or financial insight key.
 * @returns Default overline copy.
 */
export const defaultInsightOverline = (
  card: 'clinical' | 'financial',
): string =>
  card === 'clinical'
    ? DEFAULT_CLINICAL_INSIGHT_OVERLINE
    : DEFAULT_FINANCIAL_INSIGHT_OVERLINE;

/** Clinical or financial insight block with optional citation. */
export interface InsightItemDto {
  /** Small-caps label above the headline (e.g. "Clinical Insight"). */
  overline?: string;
  heading: string;
  body: string;
  citationUrl?: string;
  citationLabel?: string;
}

/** Paired clinical + financial insights on a section. */
export interface SectionInsightsDto {
  clinical?: InsightItemDto;
  financial?: InsightItemDto;
}

/** Zod schema for {@link InsightItemDto}. */
export const InsightItemSchema = z.object({
  overline: z.string().optional(),
  heading: z.string(),
  body: z.string(),
  citationUrl: z.string().optional(),
  citationLabel: z.string().optional(),
});

/** Zod schema for {@link SectionInsightsDto}. */
export const SectionInsightsSchema = z.object({
  clinical: InsightItemSchema.optional(),
  financial: InsightItemSchema.optional(),
});

/** Default inclusion rules for a library section. */
export interface SectionDefaultsDto {
  payers?: PayerType[];
  productConfigs?: ProductConfig[];
  cadences?: Cadence[];
  lifecycleMonths?: { min?: number; max?: number };
}

/** Section library metadata entry. */
export interface SectionMetaDto {
  id: SectionId;
  title: string;
  defaultOrder: number;
  supportsCallout: boolean;
  supportsNarrative: boolean;
  dataRequirements: string[];
  defaults: SectionDefaultsDto;
  rationale: string;
  /**
   * When true, the section is not ready: it defaults off, cannot be included,
   * and is omitted from preview, print, and warehouse snapshots.
   */
  disabled?: boolean;
  /**
   * Author-facing library note, such as why a section is disabled.
   * Shown on the Section library page and in the builder.
   */
  note?: string;
}

/** Slim editor reference on reports and audit events. */
export interface ReportEditorDto {
  id: string;
  name: string;
}
